import { Menu, Minus, X } from 'lucide-react'
import { IconButton } from '../ui/IconButton.jsx'
import { cn } from '../../lib/cn.js'

function trimColor(v) {
  if (v == null) return ''
  const s = typeof v === 'string' ? v.trim() : String(v).trim()
  return s
}

/**
 * Header strip for panel/overlay/slide-out layouts. Title + subtitle
 * come from the merged `conf`; close/minimize buttons are optional so
 * inline embeds (no chrome) can omit them.
 */
export function PanelHeader({
  conf,
  onClose,
  onMinimize,
  onMenuOpen,
  menuOpen = false,
  showClose = true,
  className,
}) {
  const headerBg = trimColor(conf?.widgetHeaderBackgroundColor)
  const headerFg = trimColor(conf?.widgetHeaderTextColor)
  const headerStyle = {}
  if (headerFg) headerStyle.color = headerFg
  if (headerBg) {
    headerStyle.backgroundColor = headerBg
  } else if (headerFg) {
    // Many agents set header text (often white) without a header background;
    // without a fill the panel surface shows through and chrome disappears.
    const btn = trimColor(conf?.widgetButtonColor)
    headerStyle.backgroundColor = btn || 'var(--color-brand)'
  }
  const hasCustomHeaderFg = Boolean(headerFg)
  const hasCustomHeaderBg = Boolean(headerBg) || Boolean(headerFg)

  return (
    <header
      className={cn(
        'flex items-center justify-between gap-3 border-b border-[var(--color-border)] px-4 py-3',
        !hasCustomHeaderBg && 'bg-[var(--color-surface)]',
        hasCustomHeaderFg &&
          '[&_button]:text-current [&_button]:hover:bg-black/10 [&_svg]:text-current',
        className,
      )}
      style={Object.keys(headerStyle).length ? headerStyle : undefined}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2 truncate">
        {onMenuOpen ? (
          <IconButton
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={onMenuOpen}
            className="h-8 w-8 shrink-0"
          >
            <Menu className="h-4 w-4" />
          </IconButton>
        ) : null}
        {conf?.icon && (
          <img
            src={conf.icon}
            alt=""
            className="h-7 w-7 rounded-full object-cover"
          />
        )}
        <div className="truncate">
          <h2 className="truncate text-[15px] font-semibold leading-tight">
            {conf?.widgetTitle || 'Chat'}
          </h2>
          {conf?.widgetSubtitle && (
            <p
              className={cn(
                'truncate text-xs',
                hasCustomHeaderFg
                  ? 'opacity-85'
                  : 'text-[var(--color-muted-foreground)]',
              )}
            >
              {conf.widgetSubtitle}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {onMinimize && (
          <IconButton
            aria-label="Minimize"
            onClick={onMinimize}
            className="h-8 w-8"
          >
            <Minus className="h-4 w-4" />
          </IconButton>
        )}
        {showClose && onClose && (
          <IconButton
            aria-label="Close chat"
            onClick={onClose}
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </IconButton>
        )}
      </div>
    </header>
  )
}
